import React from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import { useAuth } from './AuthContext';
import { useNotification } from './NotificationContext';
import { useTranslation } from 'react-i18next';

const EventContext = React.createContext();
export const useEvents = () => React.useContext(EventContext);

// API-Instanz außerhalb der Komponente erstellen (wird nur einmal erstellt)
const api = axios.create({
    baseURL: process.env.NODE_ENV === 'production' ? process.env.REACT_APP_API_URL : undefined,
    withCredentials: true
});

export const EventProvider = ({ children }) => {
    const { isAuthenticated, user } = useAuth();
    const { showNotification } = useNotification();
    const { t } = useTranslation();
    const [events, setEvents] = React.useState([]);
    const [error, setError] = React.useState(null);
    const socketRef = React.useRef(null);
    // NEU: Wir speichern die aktuellen Kartengrenzen hier im Context
    const mapBoundsRef = React.useRef(null);

    // Berechne die Anzahl der ausstehenden Anfragen für Events, die der User erstellt hat
    const pendingRequestCount = React.useMemo(() => {
        if (!user) return 0;
        return events.reduce((count, event) => {
            if (event.user_id === user.id && event.participants) {
                const pendingCount = event.participants.filter(p => p.status === 'pending').length;
                return count + pendingCount;
            }
            return count;
        }, 0);
    }, [events, user]);

    // Diese Funktion wird jetzt von der MapPage aufgerufen, um die Grenzen zu speichern
    const setMapBounds = React.useCallback((bounds) => {
        mapBoundsRef.current = bounds;
    }, []);

    // Diese Funktion lädt Events für die aktuell gespeicherten Grenzen.
    const refetchEventsForCurrentView = React.useCallback(() => {
        if (!isAuthenticated) return;
        const bounds = mapBoundsRef.current;
        if (bounds) {
            api.get(`/api/events?minLat=${bounds.getSouth()}&maxLat=${bounds.getNorth()}&minLon=${bounds.getWest()}&maxLon=${bounds.getEast()}`)
               .then(res => setEvents(res.data))
               .catch(err => setError("Oops, couldn't fetch the vibes."));
        } else {
            // Fallback, wenn noch keine Grenzen gesetzt sind (nur beim allerersten Laden)
            navigator.geolocation.getCurrentPosition(
                (pos) => api.get(`/api/events?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}`).then(res => setEvents(res.data)),
                () => api.get('/api/events').then(res => setEvents(res.data))
            );
        }
    }, [isAuthenticated]);

    React.useEffect(() => {
        if (isAuthenticated) {
            refetchEventsForCurrentView(); // Initiales Laden

            if (socketRef.current) socketRef.current.disconnect();
            // In development: connect to same origin (proxy handles it)
            // In production: connect to API URL
            const socketUrl = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_API_URL : window.location.origin;
            socketRef.current = io(socketUrl);
            const socket = socketRef.current;

            // WICHTIG: Die Socket-Nachrichten lösen jetzt das Neuladen für den aktuellen View aus.
            socket.on('newEvent', refetchEventsForCurrentView);
            socket.on('eventUpdated', refetchEventsForCurrentView);
            socket.on('eventDeleted', refetchEventsForCurrentView);

            // Wenn jemand bei meinem Event anklopft, zeige Notification
            socket.on('joinRequestReceived', (data) => {
                if (user && data.creatorId === user.id) {
                    showNotification(t('join_request_notification', { name: data.requesterName, event: data.eventTitle }) || `${data.requesterName} wants to join "${data.eventTitle}"`, data.eventId);
                }
            });

            // Wenn meine Anfrage akzeptiert/abgelehnt wurde
            socket.on('requestStatusChanged', (data) => {
                if (user && data.participantId === user.id) {
                    if (data.status === 'accepted') {
                        showNotification(t('request_accepted_notification', { event: data.eventTitle }) || `Your request for "${data.eventTitle}" was accepted!`, data.eventId);
                    } else {
                        showNotification(t('request_rejected_notification', { event: data.eventTitle }) || `Your request for "${data.eventTitle}" was rejected`, data.eventId);
                    }
                }
            });

            // Wenn ein Event abgesagt wurde, bei dem ich Teilnehmer bin
            socket.on('eventCanceled', (data) => {
                if (user && data.participantId === user.id) {
                    showNotification(t('event_canceled_notification', { event: data.eventTitle }) || `The event "${data.eventTitle}" was canceled`);
                }
            });

            // Wenn jemand mein Event verlässt
            socket.on('participantLeft', (data) => {
                if (user && data.creatorId === user.id) {
                    showNotification(t('participant_left_notification', { name: data.leaverName, event: data.eventTitle }) || `${data.leaverName} left "${data.eventTitle}"`, data.eventId);
                }
            });

            return () => {
                socket.off('newEvent', refetchEventsForCurrentView);
                socket.off('eventUpdated', refetchEventsForCurrentView);
                socket.off('eventDeleted', refetchEventsForCurrentView);
                socket.off('joinRequestReceived');
                socket.off('requestStatusChanged');
                socket.off('eventCanceled');
                socket.off('participantLeft');
                socket.disconnect();
            };
        }
    }, [isAuthenticated, refetchEventsForCurrentView, user, showNotification, t]);

    const fetchEventsByBounds = React.useCallback((bounds) => {
        if (!isAuthenticated) return;
        setMapBounds(bounds); // Speichere die neuen Grenzen
        api.get(`/api/events?minLat=${bounds.getSouth()}&maxLat=${bounds.getNorth()}&minLon=${bounds.getWest()}&maxLon=${bounds.getEast()}`)
           .then(res => setEvents(res.data))
           .catch(err => setError("Oops, couldn't fetch the vibes."));
    }, [isAuthenticated, setMapBounds]);

    const createEvent = async (eventData) => {
        const response = await api.post('/api/events/create', eventData);
        // Nach erfolgreichem Erstellen, Events neu laden
        refetchEventsForCurrentView();
        return response;
    };

    const joinEvent = (eventId) => api.post(`/api/events/${eventId}/join`);
    const updateParticipantStatus = (eventId, participantId, status) => api.put(`/api/events/${eventId}/participant`, { participant_id: participantId, status });
    const deleteEvent = (eventId) => api.delete(`/api/events/${eventId}`);
    const leaveEvent = (eventId) => api.delete(`/api/events/${eventId}/leave`);
    
    const value = React.useMemo(() => ({
        events, error, createEvent, joinEvent, updateParticipantStatus, deleteEvent, leaveEvent, fetchEventsByBounds, setMapBounds, pendingRequestCount
    }), [events, error, createEvent, joinEvent, updateParticipantStatus, deleteEvent, leaveEvent, fetchEventsByBounds, setMapBounds, pendingRequestCount]);
    
    return <EventContext.Provider value={value}>{children}</EventContext.Provider>;
};
